import { api } from "@/lib/api";
import { LifecycleStageCheckboxGroup } from "@/components/LifecycleStageCheckboxGroup";
import { SenderIdentitySelect } from "@/components/SenderIdentitySelect";
import {
  Button,
  Checkbox,
  CheckboxField,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Input,
  Label,
  Switch,
  SwitchField,
  Textarea,
} from "@a7y/tailwind-catalyst";
import type {
  AnnouncementDoc,
  AnnouncementUpdateRequest,
} from "@a7y/types";
import type { OrgBillingStatus, SenderIdentity } from "@a7y/types";
import * as Headless from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BILLING_STATUS_OPTIONS, isWeeklyBulletin } from "./constants";

interface AnnouncementEditDialogProps {
  announcement: AnnouncementDoc;
  open: boolean;
  onClose: () => void;
}

export function AnnouncementEditDialog({
  announcement,
  open,
  onClose,
}: AnnouncementEditDialogProps) {
  const queryClient = useQueryClient();
  // A7Y-5621: a bulletin's summary is its email body, one line per section,
  // and it is sent only by Broadcast — so no word-count hint and no Active switch.
  const bulletin = isWeeklyBulletin(announcement);

  const [title, setTitle] = useState(announcement.title);
  const [subject, setSubject] = useState(announcement.subject);
  const [summary, setSummary] = useState(announcement.summary);
  const [blogPostUrl, setBlogPostUrl] = useState(announcement.blogPostUrl);
  const [senderIdentity, setSenderIdentity] = useState<SenderIdentity>(
    announcement.senderIdentity
  );
  const [targetLifecycleStages, setTargetLifecycleStages] = useState<
    OrgBillingStatus[]
  >(announcement.targetLifecycleStages);
  const [includeChurned, setIncludeChurned] = useState(
    announcement.includeChurned
  );
  const [active, setActive] = useState(announcement.active);

  // No prop→state sync effect: AnnouncementListTab renders this only while
  // `editingAnnouncement` is non-null and keys it by announcement id, so a
  // different announcement always arrives as a fresh mount and the useState
  // initializers above already read it.

  const updateMutation = useMutation({
    mutationFn: (data: AnnouncementUpdateRequest) =>
      api.updateAnnouncement(announcement.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      onClose();
    },
  });

  const canSubmit =
    title.trim().length > 0 &&
    subject.trim().length > 0 &&
    summary.trim().length > 0 &&
    blogPostUrl.trim().length > 0 &&
    targetLifecycleStages.length > 0;

  const wordCount = summary
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0).length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    updateMutation.mutate({
      title: title.trim(),
      subject: subject.trim(),
      summary: summary.trim(),
      blogPostUrl: blogPostUrl.trim(),
      senderIdentity,
      targetLifecycleStages,
      includeChurned,
      ...(bulletin ? {} : { active }),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>Edit Announcement</DialogTitle>
      <DialogBody>
        <form
          id="edit-announcement-form"
          onSubmit={handleSubmit}
          className="space-y-5"
        >
          {/* Title */}
          <Headless.Field>
            <Label>Title</Label>
            <Input
              className="mt-2"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
              required
            />
          </Headless.Field>

          {/* Subject */}
          <Headless.Field>
            <Label>Email Subject</Label>
            <Input
              className="mt-2"
              value={subject}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSubject(e.target.value)
              }
              required
            />
          </Headless.Field>

          {/* Summary */}
          <Headless.Field>
            <Label>
              {bulletin ? "Email summary (one line per section)" : "Summary"}
            </Label>
            <Textarea
              className="mt-2"
              rows={5}
              value={summary}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setSummary(e.target.value)
              }
              required
            />
            {!bulletin && (
              <p className="mt-1.5 text-sm text-zinc-500 dark:text-zinc-400">
                {wordCount} {wordCount === 1 ? "word" : "words"}
                {wordCount > 0 && wordCount < 150 &&
                  " -- below recommended minimum of 150"}
                {wordCount > 200 &&
                  " -- above recommended maximum of 200"}
              </p>
            )}
          </Headless.Field>

          {/* Blog Post URL */}
          <Headless.Field>
            <Label>Blog Post URL</Label>
            <Input
              className="mt-2"
              type="url"
              value={blogPostUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setBlogPostUrl(e.target.value)
              }
              required
            />
          </Headless.Field>

          {/* Sender Identity */}
          <SenderIdentitySelect
            value={senderIdentity}
            onChange={(v) => setSenderIdentity(v as SenderIdentity)}
          />

          {/* Target Lifecycle Stages */}
          <LifecycleStageCheckboxGroup
            legend="Target Lifecycle Stages"
            options={BILLING_STATUS_OPTIONS}
            selected={targetLifecycleStages}
            onChange={setTargetLifecycleStages}
          />

          {/* Include Churned */}
          <CheckboxField>
            <Checkbox
              checked={includeChurned}
              onChange={(checked: boolean) => setIncludeChurned(checked)}
              color="blue"
            />
            <Label>Include Churned Users</Label>
          </CheckboxField>

          {/* Active Toggle — a bulletin is sent only by Broadcast */}
          {!bulletin && (
            <SwitchField>
              <Label>Active</Label>
              <Switch
                checked={active}
                onChange={(checked: boolean) => setActive(checked)}
                color="blue"
              />
            </SwitchField>
          )}

          {/* Error feedback */}
          {updateMutation.isError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {updateMutation.error?.message ||
                "Failed to update announcement"}
            </div>
          )}
        </form>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="edit-announcement-form"
          color="blue"
          disabled={!canSubmit || updateMutation.isPending}
        >
          {updateMutation.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
